import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Share2, Download, Copy, X, Loader2, Video, Sparkles, MessageCircle, AlertCircle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VideoShareModal = ({ open, onClose, attempt, challenge, athlete, rank }) => {
  const { getAuthHeaders } = useAuth();
  const [preparing, setPreparing] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sharingNative, setSharingNative] = useState(false);

  const brandedUrl = attempt?.branded_video_url || attempt?.video_url || '';
  const isTime = challenge?.scoring_type === 'time';
  const scoreLabel = isTime ? `${attempt?.score}s` : attempt?.score;
  const text =
    `${athlete?.name || athlete?.athlete_name || 'I'} scored ${scoreLabel} on the Meybra ${challenge?.title} Challenge${rank ? ` — ranked #${rank}` : ''}! 🔥\nCompete at app.meybra.com #MeybraChallenge`;

  // Pre-load the branded video as a File on open so native share is instant
  useEffect(() => {
    if (!open || !brandedUrl) return;
    let cancelled = false;
    setPreparing(true);
    setVideoFile(null);
    (async () => {
      try {
        const res = await fetch(brandedUrl, { credentials: 'omit' });
        const blob = await res.blob();
        if (cancelled) return;
        const filename = `meybra-${(challenge?.title || 'challenge').toLowerCase().replace(/\s+/g, '-')}-${attempt?.score || ''}.mp4`;
        const file = new File([blob], filename, { type: blob.type || 'video/mp4' });
        setVideoFile(file);
      } catch (e) {
        console.error('Failed to fetch video for share', e);
      }
      if (!cancelled) setPreparing(false);
    })();
    return () => { cancelled = true; };
  }, [open, brandedUrl]);

  const trackShare = async (channel) => {
    if (!athlete?.id) return;
    try {
      await axios.post(
        `${API}/challenges/share-event?challenge_id=${challenge.id}&athlete_id=${athlete.id}&channel=${channel}`,
        {},
        { headers: getAuthHeaders() }
      );
    } catch (e) {}
  };

  const nativeShare = async () => {
    if (!videoFile) return toast.error('Video still preparing…');
    setSharingNative(true);
    try {
      if (navigator.canShare && navigator.canShare({ files: [videoFile] })) {
        await navigator.share({
          files: [videoFile],
          title: 'Meybra Weekly Challenge',
          text,
        });
        trackShare('native-video');
        toast.success('Shared!');
      } else if (navigator.share) {
        // Some Androids accept text+url share but not files — fall back to URL share
        await navigator.share({ title: 'Meybra Weekly Challenge', text, url: brandedUrl });
        trackShare('native-link');
      } else {
        toast.error('Native share not supported on this device — use Download or WhatsApp');
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        toast.error('Share cancelled or failed');
      }
    }
    setSharingNative(false);
  };

  const downloadVideo = async () => {
    if (!videoFile) {
      // fallback: open the URL directly
      if (brandedUrl) window.open(brandedUrl, '_blank');
      return;
    }
    const url = URL.createObjectURL(videoFile);
    const a = document.createElement('a');
    a.href = url;
    a.download = videoFile.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    trackShare('download-video');
    toast.success('Video downloaded');
  };

  const shareWhatsapp = () => {
    // WhatsApp doesn't accept video upload via wa.me — share text+link, user opens link to save
    const url = `https://wa.me/?text=${encodeURIComponent(text + '\n\n' + brandedUrl)}`;
    window.open(url, '_blank');
    trackShare('whatsapp');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(brandedUrl);
      setCopied(true);
      trackShare('copy-link');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="video-share-modal">
      <div className="bg-card border border-primary/30 rounded-sm max-w-md w-full p-6 relative my-8">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
          <X size={20} />
        </button>
        <div className="text-center mb-5">
          <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <Video className="text-primary" size={28} />
          </div>
          <h3 className="font-heading text-xl uppercase tracking-tight">Share Your Video</h3>
          <p className="text-muted-foreground text-xs mt-1">
            Already branded with Meybra overlays — ready for Reels, TikTok & Stories.
          </p>
        </div>

        {/* Preview */}
        <div className="bg-black rounded-sm overflow-hidden mb-4 aspect-[9/16] max-h-72 mx-auto flex items-center justify-center">
          {brandedUrl && (
            <video
              src={brandedUrl}
              controls
              playsInline
              className="w-full h-full object-contain"
              data-testid="video-share-preview"
            />
          )}
        </div>

        {preparing && (
          <div className="flex items-center gap-2 justify-center text-xs text-muted-foreground mb-3">
            <Loader2 size={14} className="animate-spin" /> Preparing video for sharing…
          </div>
        )}

        {/* Primary: native share */}
        <Button
          onClick={nativeShare}
          disabled={preparing || sharingNative || !videoFile}
          className="w-full bg-primary text-black hover:bg-primary/90 uppercase tracking-wider font-bold h-12 mb-3"
          data-testid="video-share-native-btn"
        >
          {sharingNative ? (
            <><Loader2 size={16} className="mr-2 animate-spin" /> Opening share…</>
          ) : (
            <><Sparkles size={16} className="mr-2" /> Share Now</>
          )}
        </Button>

        {/* Secondary actions */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <Button onClick={shareWhatsapp} variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 uppercase tracking-wider text-xs" data-testid="video-share-whatsapp">
            <MessageCircle size={14} className="mr-1" /> WhatsApp
          </Button>
          <Button onClick={downloadVideo} variant="outline" disabled={preparing} className="border-primary/40 text-primary hover:bg-primary/10 uppercase tracking-wider text-xs" data-testid="video-share-download">
            <Download size={14} className="mr-1" /> MP4
          </Button>
          <Button onClick={copyLink} variant="outline" className="border-border text-muted-foreground hover:bg-muted uppercase tracking-wider text-xs" data-testid="video-share-copy">
            {copied ? <><Check size={14} className="mr-1 text-emerald-400" /> Copied</> : <><Copy size={14} className="mr-1" /> Link</>}
          </Button>
        </div>

        {/* Mobile tip */}
        <div className="bg-muted/40 border border-border/40 rounded-sm p-3 flex items-start gap-2">
          <AlertCircle size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            <span className="font-bold text-foreground">Mobile tip:</span> "Share Now" opens your device's share sheet — pick Instagram, TikTok, WhatsApp or any app installed. On desktop, use Download MP4 or WhatsApp.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VideoShareModal;
